import { useEffect, useState } from 'react';
import { useParams, Navigate, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { locationsApi } from '@/api/endpoints/locations';
import { SEOHead } from '@/components/seo';
import { PageSpinner } from '@/components/ui';
import { useFilters } from '@/contexts/FilterContext';
import { generateLocationSchema, generateBreadcrumbSchema } from '@/lib/structured-data';
import { GuideLinks } from '@/features/guides';

/**
 * LocationPage - A wrapper that sets the location context and renders child routes
 *
 * URL: /city/:slug
 * URL: /city/:slug/specials (default)
 * URL: /city/:slug/events
 * URL: /city/:slug/happy-hours
 * URL: /city/:slug/bars
 */
export function LocationPage() {
  const { slug } = useParams<{ slug: string }>();
  const { setLocation } = useFilters();
  const [locationReady, setLocationReady] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['location', slug],
    queryFn: () => locationsApi.getBySlug(slug!),
    enabled: !!slug,
  });

  // Fetch stats for SEO content (cached, non-blocking)
  const { data: statsData } = useQuery({
    queryKey: ['location-stats', slug],
    queryFn: () => locationsApi.getStats(slug!),
    enabled: !!slug && !!data?.location,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Set the location coordinates when data loads
  // IMPORTANT: Must complete before rendering Outlet so child queries use correct coordinates
  useEffect(() => {
    if (data?.location) {
      const { location } = data;
      const label = location.state
        ? `${location.name}, ${location.state}`
        : location.name;
      setLocation(
        parseFloat(String(location.latitude)),
        parseFloat(String(location.longitude)),
        'city',
        label
      );
      setLocationReady(true);
    }
  }, [data, setLocation]);

  // Reset locationReady when slug changes
  useEffect(() => {
    setLocationReady(false);
  }, [slug]);

  if (isLoading || !locationReady) {
    return <PageSpinner />;
  }

  if (isError || !data?.location) {
    return <Navigate to="/specials" replace />;
  }

  const { location } = data;
  const locationLabel = location.state
    ? `${location.name}, ${location.state}`
    : location.name;

  const stats = statsData?.stats;
  const seoDescription = stats
    ? `Browse ${stats.barCount.toLocaleString()} bars with ${stats.specialCount.toLocaleString()} specials and ${stats.eventCount.toLocaleString()} events in ${locationLabel}. Find happy hours, daily deals, and bar events updated daily.`
    : `Discover bars with specials, happy hours, and events in ${locationLabel}. Find the best deals near you.`;

  // Generate structured data for SEO
  const locationSchema = generateLocationSchema(location);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: locationLabel, path: `/city/${location.slug}` },
  ]);

  return (
    <>
      <SEOHead
        title={`Bar Specials in ${locationLabel}`}
        description={seoDescription}
        canonicalPath={`/city/${location.slug}`}
        structuredData={[locationSchema, breadcrumbSchema]}
      />

      {/* Location header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold">Bar Specials in {locationLabel}</h1>
        {stats && (
          <p className="text-sm text-[var(--color-muted)] mt-1">
            {stats.barCount.toLocaleString()} bars · {stats.specialCount.toLocaleString()} specials · {stats.eventCount.toLocaleString()} events
          </p>
        )}
      </div>

      {/* Guide articles for this city */}
      <GuideLinks locationSlug={location.slug} />

      {/* Render the child route (specials, events, etc.) */}
      <Outlet />
    </>
  );
}
